<template>
  <div class="checklist-table-header" data-locator="table-header">
    <div class="checklist-table-first-column checklist-table-header-cell">
      <v-checkbox
        v-show="isCurrentUserPm"
        v-model="allTableItemsAreSelected"
        readonly
        hide-details
        data-locator="select-all-checkbox"
        @click.stop="toggleSelectAll"
      />
    </div>

    <div
      class="checklist-table-column-title checklist-table-header-cell"
      :class="{ 'with-border': isTableHorizontallyScrolled }"
    >
      <div class="mr-1">
        {{ t('task') }}
      </div>

      <AppTooltip>
        <template #activator="{ props }">
          <app-button
            type="icon"
            class="expand-all-button"
            data-locator="expand-all-button"
            v-bind="props"
            size="small"
            @click="expandAllSections"
          >
            <app-material-icon name="expand_all" color="inherit" />
          </app-button>
        </template>
        {{ t("expandAllTooltip") }}
      </AppTooltip>
      <AppTooltip>
        <template #activator="{ props }">
          <app-button
            type="icon"
            class="collapse-all-button"
            data-locator="collapse-all-button"
            v-bind="props"
            size="small"
            @click="collapseAllSections"
          >
            <app-material-icon name="collapse_all" color="inherit" />
          </app-button>
        </template>
        {{ t("collapseAllTooltip") }}
      </AppTooltip>
    </div>

    <div
      v-for="({ label, key }) in computedHeaders"
      :key="key"
      class="checklist-table-header-cell"
    >
      <TruncatedText>{{ label }}</TruncatedText>
    </div>

    <div class="checklist-table-column-action checklist-table-header-cell" />
  </div>
</template>

<script lang="ts">
import { defineComponent, inject } from 'vue'
import { mapActions, mapGetters, mapState } from 'vuex'

import AppTooltip from '@/common/AppTooltip.vue'
import TruncatedText from '@/common/TruncatedText.vue'
import { useTranslation } from '@/common/utils/translation'
import {
  ADD_ALL_ITEMS_TO_SELECTION,
  CLEAR_ITEMS_SELECTION,
  COLLAPSE_ALL_SECTIONS,
  EXPAND_ALL_SECTIONS,
} from '@/store/modules/checklist/action_types'

export default defineComponent({
  name: 'ChecklistTableHeader',
  components: { AppTooltip, TruncatedText },
  setup () {
    const isTableHorizontallyScrolled = inject<boolean>('isTableHorizontallyScrolled')
    const { t } = useTranslation({ keyPrefix: 'project.checklist.ChecklistTableHeader' })

    return {
      isTableHorizontallyScrolled,
      t,
    }
  },
  computed: {
    ...mapState('checklist', ['checklistTableHeader']),
    ...mapGetters('checklist', [
      'allSelectedChecklistItems',
      'isChecklistFiltered',
      'selectableChecklistItems',
      'selectedTasks',
    ]),
    ...mapGetters('room', ['isCurrentUserPm']),
    allTableItemsAreSelected: {
      get (): boolean {
        return this.selectableChecklistItems.length > 0 && this.displayedSelection.length === this.selectableChecklistItems.length
      },
      set (value: boolean) {
        if (value) {
          this.ADD_ALL_ITEMS_TO_SELECTION()
        } else {
          this.CLEAR_ITEMS_SELECTION()
        }
      },
    },
    computedHeaders() {
      return this.checklistTableHeader.map((header: string) => {
        if (header === 'newColumn') {
          return { key: header, label: 'newColumn' };
        }
        return { key: header, label: this.t(header) }
      })
    },
    displayedSelection () {
      return this.isChecklistFiltered ? this.selectedTasks : this.allSelectedChecklistItems
    },
  },
  methods: {
    ...mapActions('checklist', [
      ADD_ALL_ITEMS_TO_SELECTION,
      CLEAR_ITEMS_SELECTION,
      COLLAPSE_ALL_SECTIONS,
      EXPAND_ALL_SECTIONS,
    ]),
    expandAllSections () {
      this.EXPAND_ALL_SECTIONS()
    },
    collapseAllSections () {
      this.COLLAPSE_ALL_SECTIONS()
    },
    toggleSelectAll () {
      this.allTableItemsAreSelected = !this.allTableItemsAreSelected
    },
  },
})
</script>

<style scoped lang="scss">
@import "@/scss/border-tokens";

@import "@/project/checklist/table/checklist-table.scss";
@import "@/scss/elevation-tokens";
@import "@/scss/typography-tokens";

.checklist-table-column-title {
  display: flex;
  align-items: center;
}

.checklist-table-header {
  height: 40px;
  display: grid;
  grid-template-columns: subgrid;
  grid-column: row-start / row-end;
  @include border-radius('border-radius-4');
  background-color: rgb(var(--v-theme-surface-secondary));
  position: sticky;
  top: 0;
  @include elevation('z-index-sticky-header');
}

.checklist-table-header-cell {
  @include text('heading-xs-bold');

  align-items: center;
  background-color: rgb(var(--v-theme-surface-secondary));
  color: rgb(var(--v-theme-text-primary));
  display: flex;
  padding-left: 12px;

  &:hover {
    cursor: default;
  }
}
</style>
